import { LogLevel, type LogProps } from '@cabal/shared/util/seq-log';

export const IS_SEQ_LOG_ENABLED =
  process.env.NODE_ENV !== 'production' &&
  process.env.NEXT_PUBLIC_SEQ_LOG_ENABLED === 'true';
const SEQ_LOG_APP_NAME = 'CABAL';
const SEQ_LOG_LAYER = 'frontend';
const SEQ_LOG_INGEST_PATH = '/seq-proxy/events/raw';
const SEQ_LOG_CONTENT_TYPE = 'application/vnd.serilog.clef';
const SEQ_LOG_HTTP_METHOD = 'POST';

// Seq's `/api/events/raw` CLEF endpoint reads one JSON event per line, so an
// entire batch travels as a single newline-delimited body.
const SEQ_LOG_EVENT_SEPARATOR = '\n';

// Flush cadence. Events accumulate in `bufferedSeqEvents` and drain on this
// interval, so a steady emitter costs ~1 request/second instead of one request
// per event. The timer is only armed while something is actually buffered.
const SEQ_LOG_FLUSH_INTERVAL_MS = 1000;

// Buffer bounds. Whichever limit trips first drains the buffer immediately, so
// the buffer can never hold more than one limit's worth of diagnostics: a
// runaway emitter costs extra requests, never unbounded memory.
//
// The event ceiling sits just above the busiest second observed in the dev
// server's request trace (156 events), so an ordinary burst still lands in a
// single flush.
const SEQ_LOG_MAX_BUFFERED_EVENTS = 160;
// The character ceiling bounds memory when individual events carry large
// payloads (DOM snapshots, serialized layout probes), and keeps every flushed
// body — including the teardown beacon — clear of the 64 KB payload quota that
// `navigator.sendBeacon` and `fetch({ keepalive: true })` both enforce.
const BYTES_PER_KIBIBYTE = 1024;
const SEQ_LOG_MAX_BUFFERED_KIBIBYTES = 32;
const SEQ_LOG_MAX_BUFFERED_CHARS =
  SEQ_LOG_MAX_BUFFERED_KIBIBYTES * BYTES_PER_KIBIBYTE;

// Teardown hooks. A page can disappear between two flush ticks, so the buffer
// is also drained when the document goes hidden and when the page unloads.
const SEQ_LOG_VISIBILITY_CHANGE_EVENT: keyof DocumentEventMap =
  'visibilitychange';
const SEQ_LOG_PAGE_HIDE_EVENT: keyof WindowEventMap = 'pagehide';
const SEQ_LOG_HIDDEN_VISIBILITY: DocumentVisibilityState = 'hidden';

/**
 * How a drained batch reaches the ingest proxy. `Deferred` is the normal path.
 * `Teardown` runs while the page is going away, where a plain `fetch` is not
 * guaranteed to outlive the document.
 */
enum SeqLogDeliveryMode {
  Deferred = 'deferred',
  Teardown = 'teardown',
}

interface EmitSeqLogInput {
  msg: string;
  component: string;
  context?: LogProps;
  props?: LogProps;
  level?: LogLevel;
}

const bufferedSeqEvents: string[] = [];
let bufferedSeqEventChars = 0;
let scheduledFlushId: ReturnType<typeof setTimeout> | undefined;
let areTeardownFlushListenersRegistered = false;

function buildSeqEvent({
  msg,
  component,
  context,
  props,
  level,
}: Required<Pick<EmitSeqLogInput, 'msg' | 'component' | 'level'>> &
  Pick<EmitSeqLogInput, 'context' | 'props'>): LogProps {
  return {
    '@t': new Date().toISOString(),
    '@l': level,
    '@mt': msg,
    app: SEQ_LOG_APP_NAME,
    layer: SEQ_LOG_LAYER,
    component,
    ...context,
    ...props,
  };
}

function serializeSeqEvent(
  event: LogProps,
  msg: string,
  component: string,
): string {
  try {
    return JSON.stringify(event);
  } catch {
    return JSON.stringify({
      '@t': new Date().toISOString(),
      '@l': LogLevel.Warning,
      '@mt': 'Frontend Seq event serialization failed',
      app: SEQ_LOG_APP_NAME,
      layer: SEQ_LOG_LAYER,
      component,
      originalMessage: msg,
    });
  }
}

function postSeqBatch(body: string): void {
  try {
    void fetch(SEQ_LOG_INGEST_PATH, {
      method: SEQ_LOG_HTTP_METHOD,
      headers: { 'Content-Type': SEQ_LOG_CONTENT_TYPE },
      body,
    }).catch(() => {
      // Diagnostics must never affect application behavior.
    });
  } catch {
    // Diagnostics must never affect application behavior.
  }
}

function beaconSeqBatch(body: string): boolean {
  try {
    if (
      typeof navigator === 'undefined' ||
      typeof navigator.sendBeacon !== 'function'
    ) {
      return false;
    }
    return navigator.sendBeacon(
      SEQ_LOG_INGEST_PATH,
      new Blob([body], { type: SEQ_LOG_CONTENT_TYPE }),
    );
  } catch {
    return false;
  }
}

function postSeqBatchOnTeardown(body: string): void {
  if (beaconSeqBatch(body)) return;

  try {
    void fetch(SEQ_LOG_INGEST_PATH, {
      method: SEQ_LOG_HTTP_METHOD,
      headers: { 'Content-Type': SEQ_LOG_CONTENT_TYPE },
      body,
      keepalive: true,
    }).catch(() => {
      // Diagnostics must never affect application behavior.
    });
  } catch {
    // Diagnostics must never affect application behavior.
  }
}

const SEQ_LOG_DELIVERY: Record<SeqLogDeliveryMode, (body: string) => void> = {
  [SeqLogDeliveryMode.Deferred]: postSeqBatch,
  [SeqLogDeliveryMode.Teardown]: postSeqBatchOnTeardown,
};

function cancelScheduledSeqLogFlush(): void {
  if (scheduledFlushId === undefined) return;
  clearTimeout(scheduledFlushId);
  scheduledFlushId = undefined;
}

function flushSeqLogBuffer(mode: SeqLogDeliveryMode): void {
  cancelScheduledSeqLogFlush();
  if (bufferedSeqEvents.length === 0) return;

  const batch = bufferedSeqEvents.splice(0, bufferedSeqEvents.length);
  bufferedSeqEventChars = 0;
  SEQ_LOG_DELIVERY[mode](batch.join(SEQ_LOG_EVENT_SEPARATOR));
}

function scheduleSeqLogFlush(): void {
  if (scheduledFlushId !== undefined) return;
  scheduledFlushId = setTimeout(() => {
    scheduledFlushId = undefined;
    flushSeqLogBuffer(SeqLogDeliveryMode.Deferred);
  }, SEQ_LOG_FLUSH_INTERVAL_MS);
}

function isSeqLogBufferFull(): boolean {
  return (
    bufferedSeqEvents.length >= SEQ_LOG_MAX_BUFFERED_EVENTS ||
    bufferedSeqEventChars >= SEQ_LOG_MAX_BUFFERED_CHARS
  );
}

function bufferSeqEvent(serializedEvent: string): void {
  bufferedSeqEvents.push(serializedEvent);
  bufferedSeqEventChars += serializedEvent.length;

  if (isSeqLogBufferFull()) {
    flushSeqLogBuffer(SeqLogDeliveryMode.Deferred);
    return;
  }

  scheduleSeqLogFlush();
}

function handleSeqLogVisibilityChange(): void {
  if (document.visibilityState !== SEQ_LOG_HIDDEN_VISIBILITY) return;
  flushSeqLogBuffer(SeqLogDeliveryMode.Teardown);
}

function handleSeqLogPageHide(): void {
  flushSeqLogBuffer(SeqLogDeliveryMode.Teardown);
}

function registerSeqLogTeardownFlush(): void {
  if (areTeardownFlushListenersRegistered) return;
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  areTeardownFlushListenersRegistered = true;
  document.addEventListener(
    SEQ_LOG_VISIBILITY_CHANGE_EVENT,
    handleSeqLogVisibilityChange,
  );
  window.addEventListener(SEQ_LOG_PAGE_HIDE_EVENT, handleSeqLogPageHide);
}

/**
 * Drain the buffer immediately using the teardown transport (beacon, then
 * `keepalive` fetch).
 *
 * Call this before deliberately navigating the document away — a normal flush
 * is a plain `fetch` that the unload can cancel, and the `pagehide` listener
 * runs too late to help a caller that wants its last event guaranteed. The
 * forced-sign-out path is the case that matters: losing that event is losing
 * the only record of why a user was signed out.
 */
export function flushSeqLogBeforeNavigation(): void {
  if (!IS_SEQ_LOG_ENABLED) return;
  flushSeqLogBuffer(SeqLogDeliveryMode.Teardown);
}

/**
 * Non-hook Seq emitter for the browser transport. Use from class components,
 * error handlers, or any non-React-render site that can't call `useSeqLog`.
 * No-op outside a Seq-enabled development build; never throws.
 *
 * Events are buffered and shipped as one newline-delimited CLEF batch per
 * `SEQ_LOG_FLUSH_INTERVAL_MS`, so delivery is asynchronous: an event reaches
 * Seq up to one interval after it is emitted. Order within a batch is emit
 * order, and every event carries the `@t` stamped at emit time.
 */
export function emitSeqLog(input: EmitSeqLogInput): void {
  if (!IS_SEQ_LOG_ENABLED) return;
  registerSeqLogTeardownFlush();
  const event = buildSeqEvent({
    msg: input.msg,
    component: input.component,
    context: input.context,
    props: input.props,
    level: input.level ?? LogLevel.Debug,
  });
  bufferSeqEvent(serializeSeqEvent(event, input.msg, input.component));
}
